import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Search,
  Filter,
  Edit,
  Trash2,
  UserCheck,
  UserX,
  Loader2,
  Mail,
  Phone,
  Building,
  ChevronLeft,
  ChevronRight,
  Eye,
  User,
  MapPin,
  Calendar,
  ShoppingCart,
  Package,
  CreditCard,
  Hash,
  Users,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { adminAPI } from '@/services/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ITEMS_PER_PAGE = 50;
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [editUser, setEditUser] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userOrders, setUserOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to page 1 on new search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [page, roleFilter, debouncedSearch, sortBy, sortOrder]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: ITEMS_PER_PAGE,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (roleFilter !== 'all') params.role = roleFilter;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      const response = await adminAPI.getUsers(params);
      setUsers(response.data.users || response.data || []);
      setTotalUsers(response.data.total || response.data?.length || 0);
      setTotalPages(Math.ceil((response.data.total || response.data?.length || 0) / ITEMS_PER_PAGE));
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserOrders = async (userId) => {
    try {
      setLoadingOrders(true);
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/orders`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('grc_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUserOrders(data.orders || data || []);
      } else {
        setUserOrders([]);
      }
    } catch (error) {
      console.error('Error fetching user orders:', error);
      setUserOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleViewUser = async (user) => {
    setSelectedUser(user);
    setShowDetailDialog(true);
    await fetchUserOrders(user.id);
  };

  const handleEditUser = (user) => {
    setEditUser({ ...user });
    setShowEditDialog(true);
  };

  const handleSaveUser = async () => {
    try {
      setSaving(true);
      await adminAPI.updateUser(editUser.id, {
        first_name: editUser.first_name,
        last_name: editUser.last_name,
        role: editUser.role,
        is_active: editUser.is_active,
      });
      toast.success('User updated successfully');
      setShowEditDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await adminAPI.updateUser(user.id, { is_active: !user.is_active });
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.first_name} ${user.last_name}?`)) {
      return;
    }
    try {
      await adminAPI.deleteUser(user.id);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  // Server-side search is used, so we display users directly
  const displayUsers = users;

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-red-100 text-red-700';
      case 'admin': return 'bg-purple-100 text-purple-700';
      case 'speaker': return 'bg-green-100 text-green-700';
      case 'corporate_admin': return 'bg-blue-100 text-blue-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'payment_pending': return 'bg-orange-100 text-orange-700';
      case 'refunded': return 'bg-blue-100 text-blue-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  // Helper to format address
  const formatUserAddress = (user) => {
    // Handle nested address object
    let address, city, state, zip, country;
    
    if (user.address && typeof user.address === 'object') {
      address = user.address.street || user.address.address;
      city = user.address.city;
      state = user.address.state || user.address.zone;
      zip = user.address.postal_code || user.address.postcode || user.address.zip;
      country = user.address.country;
    } else if (typeof user.address === 'string') {
      address = user.address;
    }
    
    // Fallback to billing fields
    address = address || user.billing_address_1 || user.billing_address;
    city = city || user.billing_city;
    state = state || user.billing_state;
    zip = zip || user.billing_zip || user.billing_postcode || user.postal_code;
    country = country || user.billing_country;
    
    const parts = [address, city, state, zip, country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'No address provided';
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-navy-700">User Management</h2>
          <p className="text-slate-500">
            {totalUsers > 0 ? `Showing ${displayUsers.length} of ${totalUsers.toLocaleString()} users (Page ${page} of ${totalPages})` : 'Manage platform users and their roles'}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-slate-200 rounded-xl">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Users</p>
                <p className="text-xl font-bold text-slate-800">{totalUsers.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 rounded-xl">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Active Users</p>
                <p className="text-xl font-bold text-slate-800">
                  {users.filter(u => u.is_active).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 rounded-xl">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Admins</p>
                <p className="text-xl font-bold text-slate-800">
                  {users.filter(u => u.role === 'admin' || u.role === 'super_admin').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 rounded-xl">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <User className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Customers</p>
                <p className="text-xl font-bold text-slate-800">
                  {users.filter(u => u.role === 'customer').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 rounded-xl">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-lg"
                data-testid="search-users"
              />
            </div>
            <Select value={roleFilter} onValueChange={(value) => { setRoleFilter(value); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-48 rounded-lg">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="customer">Customers</SelectItem>
                <SelectItem value="speaker">Speakers</SelectItem>
                <SelectItem value="admin">Admins</SelectItem>
                <SelectItem value="super_admin">Super Admins</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={`${sortBy}:${sortOrder}`}
              onValueChange={(value) => {
                const [field, order] = value.split(':');
                setSortBy(field);
                setSortOrder(order);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-full sm:w-56 rounded-lg" data-testid="sort-users-select">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at:desc">Newest first (Recent signups)</SelectItem>
                <SelectItem value="created_at:asc">Oldest first</SelectItem>
                <SelectItem value="first_name:asc">First Name A-Z</SelectItem>
                <SelectItem value="first_name:desc">First Name Z-A</SelectItem>
                <SelectItem value="last_name:asc">Last Name A-Z</SelectItem>
                <SelectItem value="last_name:desc">Last Name Z-A</SelectItem>
                <SelectItem value="email:asc">Email A-Z</SelectItem>
                <SelectItem value="email:desc">Email Z-A</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="border-slate-200 rounded-xl overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-navy-500" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">
                      <button
                        type="button"
                        className="flex items-center gap-1 hover:text-navy-700 transition-colors"
                        onClick={() => {
                          if (sortBy === 'first_name') {
                            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                          } else {
                            setSortBy('first_name');
                            setSortOrder('asc');
                          }
                          setPage(1);
                        }}
                        data-testid="sort-header-name"
                      >
                        User
                        {sortBy === 'first_name' ? (
                          sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : (
                          <ArrowUpDown className="w-3 h-3 text-slate-300" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold">Contact</TableHead>
                    <TableHead className="font-semibold">Role</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">
                      <button
                        type="button"
                        className="flex items-center gap-1 hover:text-navy-700 transition-colors"
                        onClick={() => {
                          if (sortBy === 'created_at') {
                            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                          } else {
                            setSortBy('created_at');
                            setSortOrder('desc');
                          }
                          setPage(1);
                        }}
                        data-testid="sort-header-joined"
                      >
                        Joined
                        {sortBy === 'created_at' ? (
                          sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : (
                          <ArrowUpDown className="w-3 h-3 text-slate-300" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayUsers.map((user) => (
                      <TableRow key={user.id} className="hover:bg-slate-50" data-testid={`user-row-${user.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-navy-600 flex items-center justify-center text-white font-medium">
                              {user.first_name?.[0]}{user.last_name?.[0]}
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">
                                {user.first_name} {user.last_name}
                              </p>
                              <p className="text-sm text-slate-500">{user.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {user.phone && (
                              <p className="text-sm text-slate-600 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {user.phone}
                              </p>
                            )}
                            {user.company && (
                              <p className="text-sm text-slate-500 flex items-center gap-1">
                                <Building className="w-3 h-3" />
                                {user.company}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getRoleBadgeColor(user.role)} capitalize`}>
                            {user.role?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {user.created_at ? format(new Date(user.created_at), 'MMM dd, yyyy') : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleViewUser(user)}
                              className="h-8 w-8"
                              title="View Details"
                              data-testid={`view-user-${user.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditUser(user)}
                              className="h-8 w-8"
                              title="Edit User"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleToggleStatus(user)}
                              className="h-8 w-8"
                              title={user.is_active ? 'Deactivate' : 'Activate'}
                            >
                              {user.is_active ? (
                                <UserX className="w-4 h-4 text-red-500" />
                              ) : (
                                <UserCheck className="w-4 h-4 text-green-500" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteUser(user)}
                              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                              title="Delete User"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Showing {Math.min(((page - 1) * ITEMS_PER_PAGE) + 1, totalUsers)} - {Math.min(page * ITEMS_PER_PAGE, totalUsers)} of {totalUsers.toLocaleString()} users
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(1)}
            disabled={page === 1}
            className="rounded-lg"
          >
            First
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded-lg"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-slate-600 px-2">Page {page} of {totalPages}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="rounded-lg"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(totalPages)}
            disabled={page >= totalPages}
            className="rounded-lg"
          >
            Last
          </Button>
        </div>
      </div>

      {/* User Details Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh]" data-testid="user-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <User className="w-5 h-5" />
              Customer Details
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <ScrollArea className="max-h-[calc(90vh-120px)] pr-4">
              <div className="space-y-6 py-4">
                {/* User Header */}
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="w-16 h-16 rounded-full bg-navy-600 flex items-center justify-center text-white text-xl font-bold">
                    {selectedUser.first_name?.[0]}{selectedUser.last_name?.[0]}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-xl text-navy-700">
                      {selectedUser.first_name} {selectedUser.last_name}
                    </h3>
                    <p className="text-slate-500">{selectedUser.email}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className={`${getRoleBadgeColor(selectedUser.role)} capitalize`}>
                        {selectedUser.role?.replace('_', ' ')}
                      </Badge>
                      <Badge className={selectedUser.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {selectedUser.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Personal Information
                    </h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">First Name</p>
                        <p className="font-medium text-slate-800">{selectedUser.first_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Last Name</p>
                        <p className="font-medium text-slate-800">{selectedUser.last_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1">
                          <Mail className="w-3 h-3" /> Email
                        </p>
                        <p className="text-slate-700">{selectedUser.email || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1">
                          <Phone className="w-3 h-3" /> Phone
                        </p>
                        <p className="text-slate-700">{selectedUser.phone || 'N/A'}</p>
                      </div>
                      {selectedUser.company && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1">
                            <Building className="w-3 h-3" /> Company
                          </p>
                          <p className="text-slate-700">{selectedUser.company}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> Address
                        </p>
                        <p className="text-slate-700 text-sm">{formatUserAddress(selectedUser)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Information */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Account Information
                    </h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Joined</p>
                      <p className="font-medium text-slate-800">
                        {selectedUser.created_at ? format(new Date(selectedUser.created_at), 'MMMM dd, yyyy') : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Email Verified</p>
                      <Badge className={selectedUser.is_verified || selectedUser.email_verified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                        {selectedUser.is_verified || selectedUser.email_verified ? 'Verified' : 'Not Verified'}
                      </Badge>
                    </div>
                    {selectedUser.opencart_customer_id && (
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">OpenCart ID</p>
                        <p className="font-mono text-sm text-slate-700">{selectedUser.opencart_customer_id}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Orders / Registrations */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4" />
                      Orders & Registrations ({userOrders.length})
                    </h3>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {loadingOrders ? (
                      <div className="p-8 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-navy-500" />
                      </div>
                    ) : userOrders.length === 0 ? (
                      <div className="p-8 text-center text-slate-500">
                        No orders found for this customer
                      </div>
                    ) : (
                      userOrders.slice(0, 10).map((order, idx) => (
                        <div key={idx} className="p-4 hover:bg-slate-50 transition-colors">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-sm font-medium text-navy-700">
                                  {order.order_number}
                                </span>
                                {order.legacy_order_number && (
                                  <span className="text-xs text-slate-500">
                                    (Legacy: {order.legacy_order_number})
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-2 mt-2">
                                <Badge className={`${getStatusBadgeColor(order.status)} capitalize text-xs`}>
                                  {order.status?.replace('_', ' ')}
                                </Badge>
                                <Badge variant="secondary" className="text-xs">
                                  {order.items?.length || 0} items
                                </Badge>
                              </div>
                              {/* Show webinar codes */}
                              {order.items && order.items.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {order.items.slice(0, 3).map((item, i) => (
                                    <Badge key={i} variant="outline" className="text-xs font-mono">
                                      {item.webinar_code || item.code || 'N/A'}
                                    </Badge>
                                  ))}
                                  {order.items.length > 3 && (
                                    <Badge variant="outline" className="text-xs">
                                      +{order.items.length - 3} more
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="text-right shrink-0">
                              <p className="font-semibold text-slate-800">
                                ${(order.total || order.total_amount || 0).toFixed(2)}
                              </p>
                              <p className="text-xs text-slate-500 mt-1">
                                {order.created_at ? format(new Date(order.created_at), 'MMM dd, yyyy') : '-'}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                    {userOrders.length > 10 && (
                      <div className="p-3 text-center bg-slate-50">
                        <p className="text-sm text-slate-500">
                          Showing 10 of {userOrders.length} orders
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Close Button */}
                <div className="pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowDetailDialog(false)}
                    className="w-full"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit User</DialogTitle>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input
                  value={editUser.first_name || ''}
                  onChange={(e) => setEditUser({ ...editUser, first_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input
                  value={editUser.last_name || ''}
                  onChange={(e) => setEditUser({ ...editUser, last_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={editUser.role}
                  onValueChange={(value) => setEditUser({ ...editUser, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="speaker">Speaker</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={editUser.is_active}
                  onChange={(e) => setEditUser({ ...editUser, is_active: e.target.checked })}
                  className="rounded border-slate-300"
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleSaveUser} disabled={saving} className="flex-1">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
